/**
 * ErrorBoundary — prevents an uncaught render error from leaving the guest
 * on a blank/broken page. Shows a boutique-styled fallback with a Retry.
 * Also intercepts uncaught promise rejections (typical of a stale bundle
 * hitting a route/endpoint that no longer exists) so they don't surface as
 * the raw red CRA overlay.
 */
import React from "react";

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] caught", error);
  }

  componentDidMount() {
    this._onRejection = (e) => {
      // Silently swallow any unhandled Axios/network rejection.
      // Real render bugs still surface via componentDidCatch → hasError.
      const reason = e?.reason;
      const msg = reason?.message || String(reason || "");
      const isAxios =
        reason?.isAxiosError === true ||
        reason?.name === "AxiosError" ||
        /axios|timeout|network|failed to fetch|request failed with status code/i.test(msg);
      if (isAxios) {
        e.preventDefault();
        // eslint-disable-next-line no-console
        console.warn("[ErrorBoundary] swallowed unhandled network rejection:", msg);
      }
    };
    window.addEventListener("unhandledrejection", this._onRejection);
  }

  componentWillUnmount() {
    if (this._onRejection)
      window.removeEventListener("unhandledrejection", this._onRejection);
  }

  handleRetry = () => {
    // Attempt to force-fetch a fresh index.html by unregistering the SW
    // and reloading, so a stuck cached bundle never traps the guest.
    try {
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.getRegistrations().then((regs) => {
          regs.forEach((r) => r.unregister());
          if (window.caches?.keys) {
            window.caches.keys().then((keys) => keys.forEach((k) => window.caches.delete(k)));
          }
          setTimeout(() => window.location.reload(), 200);
        });
      } else {
        window.location.reload();
      }
    } catch {
      window.location.reload();
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-screen flex items-center justify-center bg-ivory-50 px-6">
        <div className="max-w-sm text-center">
          <div className="font-editorial italic text-3xl text-ink-900">
            Un attimo, per favore
          </div>
          <div className="flex justify-center my-3">
            <span className="ornament" />
          </div>
          <p className="text-sm text-ink-500 leading-relaxed">
            Si è verificato un problema temporaneo di caricamento. Ricarichiamo
            la pagina e ripristiniamo la connessione.
          </p>
          <button
            type="button"
            onClick={this.handleRetry}
            data-testid="error-boundary-retry"
            className="mt-5 inline-flex items-center justify-center rounded-full bg-bronze-500 hover:bg-bronze-600 text-white font-semibold text-sm px-5 py-2.5 transition"
          >
            Ricarica ora
          </button>
        </div>
      </div>
    );
  }
}
